import { useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Download, Eye, RefreshCw, Search } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import {
  exportUnifiedProducts,
  fetchDivisions,
  fetchPcrFields,
  fetchPcrRecords,
  fetchProductFactsSummary,
  fetchProductPimData,
  fetchProducts,
  type ProductDto,
  type ProductDataItemDto,
} from '../services/api'
import { EmptyState, FieldList, FilterPill, KpiCard, MimCard, MimPage, StatusPill, formatDateTime } from '../components/MimUi'

type SortKey = 'name' | 'code' | 'family' | 'applicationType' | 'lastComplianceCheckUtc' | 'variantCount' | 'status'

export default function Products() {
  const { user } = useAuth()
  const isAdmin = user?.businessLevel === 'MasterAdmin' || user?.businessLevel === 'Division'
  const [search, setSearch] = useState('')
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([])
  const [family, setFamily] = useState('')
  const [application, setApplication] = useState('')
  const [selected, setSelected] = useState<ProductDto | null>(null)
  const [exporting, setExporting] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', 'pcr-management'],
    queryFn: () => fetchProducts({ kingspanOnly: true }),
  })

  const { data: divisions = [] } = useQuery({
    queryKey: ['divisions', 'pcr-management'],
    queryFn: fetchDivisions,
    enabled: isAdmin,
  })

  const { data: selectedPim = [] } = useQuery({
    queryKey: ['product-pim', selected?.id, 'pcr-management'],
    queryFn: () => fetchProductPimData(selected!.id),
    enabled: !!selected,
  })

  const familyOptions = useMemo(
    () => Array.from(new Set(products.map((p) => p.family).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [products],
  )
  const applicationOptions = useMemo(
    () => Array.from(new Set(products.map((p) => p.applicationType).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [products],
  )

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase()
    let rows = products.filter((product) => {
      const matchesDivision =
        !isAdmin ||
        selectedDivisions.length === 0 ||
        selectedDivisions.some((d) => (product.division || '').split(',').map((x) => x.trim()).includes(d))
      const matchesFamily = !family || product.family === family
      const matchesApplication = !application || product.applicationType === application
      const matchesText =
        !text ||
        product.name.toLowerCase().includes(text) ||
        product.code.toLowerCase().includes(text) ||
        (product.family || '').toLowerCase().includes(text)
      return matchesDivision && matchesFamily && matchesApplication && matchesText
    })

    const dir = sortDir === 'asc' ? 1 : -1
    rows = [...rows].sort((a, b) => {
      const av = sortValue(a, sortKey)
      const bv = sortValue(b, sortKey)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
    return rows
  }, [products, search, selectedDivisions, family, application, isAdmin, sortKey, sortDir])

  const stats = useMemo(() => {
    const verified = products.filter((p) => p.isApproved || p.hasKingspanSource).length
    return {
      total: products.length,
      verified,
      exceptions: Math.max(0, products.length - verified),
      pending: products.filter((p) => !p.isApproved && !p.hasKingspanSource).length,
    }
  }, [products])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const toggleDivision = (name: string) => {
    setSelectedDivisions((prev) => (prev.includes(name) ? prev.filter((d) => d !== name) : [...prev, name]))
  }

  const exportRows = async () => {
    try {
      setExporting(true)
      const blob = await exportUnifiedProducts({ format: 'csv', search: search || undefined, family: family || undefined })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pcr-management-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const sortMark = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')

  return (
    <MimPage title="PCR Management" subtitle="Browse and manage normalised product records" compactHeader>
      <div className="mim-split">
        <div className="mim-stack mim-split-sticky-list">
          <MimCard className="pcr-management-filters">
            <div className="mim-toolbar">
              <div className="mim-search">
                <Search size={15} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products, codes..." />
              </div>
              <select className="mim-select" value={family} onChange={(e) => setFamily(e.target.value)} style={{ minWidth: 140 }}>
                <option value="">All families</option>
                {familyOptions.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <select className="mim-select" value={application} onChange={(e) => setApplication(e.target.value)} style={{ minWidth: 140 }}>
                <option value="">All applications</option>
                {applicationOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <button type="button" className="mim-btn mim-btn-secondary" disabled={exporting} onClick={() => void exportRows()}>
                <Download size={14} /> {exporting ? 'Exporting...' : 'Export'}
              </button>
              <Link to="/pcr-upload" className="mim-btn mim-btn-primary"><RefreshCw size={14} /> Sync PCR</Link>
            </div>
            {isAdmin && (
              <div className="mim-toolbar" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                <FilterPill active={selectedDivisions.length === 0} onClick={() => setSelectedDivisions([])}>All divisions</FilterPill>
                {divisions.map((d) => (
                  <FilterPill key={d.id} active={selectedDivisions.includes(d.name)} onClick={() => toggleDivision(d.name)}>
                    {d.name}
                  </FilterPill>
                ))}
              </div>
            )}
          </MimCard>

          <div className="mim-grid-4" style={{ flexShrink: 0 }}>
            <KpiCard compact label="Total Products" value={stats.total} />
            <KpiCard compact label="Verified" value={stats.verified} tone="green" />
            <KpiCard compact label="Exceptions" value={stats.exceptions} tone="orange" />
            <KpiCard compact label="Pending Check" value={stats.pending} tone="grey" />
          </div>

          <MimCard className="pcr-management-table-card">
            {isLoading ? (
              <EmptyState title="Loading product register" />
            ) : filtered.length === 0 ? (
              <EmptyState title="No products found" detail="Adjust the search or filters." />
            ) : (
              <div className="mim-table-wrap">
                <table className="mim-table">
                  <thead>
                    <tr>
                      <th className="mim-th-sort" onClick={() => toggleSort('name')}>Product Name{sortMark('name')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('code')}>Code{sortMark('code')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('family')}>Family{sortMark('family')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('applicationType')}>Application{sortMark('applicationType')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('lastComplianceCheckUtc')}>Last check{sortMark('lastComplianceCheckUtc')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('variantCount')}>Variants{sortMark('variantCount')}</th>
                      <th className="mim-th-sort" onClick={() => toggleSort('status')}>Status{sortMark('status')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, 200).map((product) => (
                      <tr
                        key={product.id}
                        onClick={() => setSelected(product)}
                        style={selected?.id === product.id ? { background: 'var(--mim-blue-pale)' } : undefined}
                      >
                        <td><strong>{product.name}</strong></td>
                        <td className="mono">{product.code}</td>
                        <td>{product.family || '-'}</td>
                        <td>{product.applicationType || '-'}</td>
                        <td>{product.lastComplianceCheckUtc ? formatDateTime(product.lastComplianceCheckUtc) : '—'}</td>
                        <td>{product.variantCount ?? 0}</td>
                        <td>
                          <StatusPill
                            status={product.isApproved || product.hasKingspanSource ? 'Verified' : 'Pending'}
                            tone={product.isApproved || product.hasKingspanSource ? 'pass' : 'pending'}
                          />
                        </td>
                        <td>
                          <button type="button" className="mim-icon-button" onClick={(e) => { e.stopPropagation(); setSelected(product) }}>
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mim-table-footer">Showing {Math.min(filtered.length, 200)} of {filtered.length} products</div>
              </div>
            )}
          </MimCard>
        </div>

        <div className="mim-split-sticky-detail">
          {selected ? (
            <ProductDetailPanel product={selected} pim={selectedPim} />
          ) : (
            <MimCard title="Product detail">
              <EmptyState title="No product selected" detail="Select a product from the list to review its PCR fields and create a check." />
            </MimCard>
          )}
        </div>
      </div>
    </MimPage>
  )
}

function sortValue(product: ProductDto, key: SortKey): string | number {
  switch (key) {
    case 'name': return (product.name || '').toLowerCase()
    case 'code': return (product.code || '').toLowerCase()
    case 'family': return (product.family || '').toLowerCase()
    case 'applicationType': return (product.applicationType || '').toLowerCase()
    case 'lastComplianceCheckUtc': return product.lastComplianceCheckUtc ? Date.parse(product.lastComplianceCheckUtc) : 0
    case 'variantCount': return product.variantCount ?? 0
    case 'status': return product.isApproved || product.hasKingspanSource ? 1 : 0
    default: return ''
  }
}

function ProductDetailPanel({
  product,
  pim,
}: {
  product: ProductDto
  pim: ProductDataItemDto[]
}) {
  const groups = useMemo(() => {
    const byCategory = new Map<string, ProductDataItemDto[]>()
    for (const item of pim) {
      const category = item.category?.trim() || 'Other'
      const list = byCategory.get(category) ?? []
      list.push(item)
      byCategory.set(category, list)
    }
    return Array.from(byCategory.entries())
      .map(([category, items]) => ({ category, rows: collapseByValue(items) }))
      .sort((a, b) => a.category.localeCompare(b.category))
  }, [pim])

  const { data: pcrRecords = [] } = useQuery({
    queryKey: ['pcr-records', product.id],
    queryFn: () => fetchPcrRecords({ productId: product.id }),
  })
  const latestPcr = pcrRecords[0]

  const { data: pcrFields } = useQuery({
    queryKey: ['pcr-fields', latestPcr?.id],
    queryFn: () => fetchPcrFields(latestPcr!.id),
    enabled: !!latestPcr,
  })

  const { data: factsSummary } = useQuery({
    queryKey: ['product-facts-summary', product.id],
    queryFn: () => fetchProductFactsSummary(product.id),
  })

  const [showVariantMatrix, setShowVariantMatrix] = useState(false)
  const approvedPcrRecords = useMemo(() => pcrRecords.filter((r) => r.status === 'Approved'), [pcrRecords])
  const variantPcrFieldQueries = useQueries({
    queries: approvedPcrRecords.map((record) => ({
      queryKey: ['pcr-fields', record.id],
      queryFn: () => fetchPcrFields(record.id),
      enabled: showVariantMatrix,
    })),
  })
  const variantMatrix = useMemo(() => {
    if (!showVariantMatrix) return null
    const columns = approvedPcrRecords.map((record, i) => ({
      pcrId: record.id,
      label: `v${record.version}${record.productVariantId ? ` · ${record.productVariantId.slice(0, 8)}` : ' · no variant'}`,
      fields: variantPcrFieldQueries[i]?.data,
    }))
    const rowKeys = new Set<string>()
    columns.forEach((c) => c.fields?.fields.forEach((f) => rowKeys.add(f.canonicalFieldKey)))
    const rows = Array.from(rowKeys).sort().map((key) => ({
      key,
      displayName: columns.map((c) => c.fields?.fields.find((f) => f.canonicalFieldKey === key)?.displayName).find(Boolean) || key,
      values: columns.map((c) => {
        const field = c.fields?.fields.find((f) => f.canonicalFieldKey === key)
        return field ? (field.normalizedValue || field.rawValue || '-') : '-'
      }),
    }))
    return { columns, rows, isLoading: variantPcrFieldQueries.some((q) => q.isLoading) }
  }, [showVariantMatrix, approvedPcrRecords, variantPcrFieldQueries])

  return (
    <aside className="product-detail-panel">
      <MimCard title="Product detail" subtitle="Source of truth record">
        <div className="mim-grid-2" style={{ marginBottom: 14 }}>
          <Link className="mim-btn mim-btn-primary" to={`/compliance-check?productId=${product.id}`}>Create a check</Link>
          <Link className="mim-btn mim-btn-secondary" to={`/products/${product.id}`}>Open full record</Link>
        </div>

        <div className="product-detail-hero">
          <strong>{product.name}</strong>
          <span className="mim-mono">{product.code}</span>
          <div>
            {product.family && <StatusPill status={product.family} tone="warning" />}
            {product.applicationType && <StatusPill status={product.applicationType} tone="info" />}
          </div>
        </div>

        <details className="mim-collapse-section" open>
          <summary>Product identity</summary>
          <FieldList rows={[
            { label: 'Product Name', value: product.name },
            { label: 'Product Code', value: product.code },
            { label: 'Family', value: product.family || '-' },
            { label: 'Application', value: product.applicationType || '-' },
            { label: 'Variants', value: String(product.variantCount ?? 0) },
            { label: 'Last check', value: product.lastComplianceCheckUtc ? formatDateTime(product.lastComplianceCheckUtc) : '—' },
          ]} />
        </details>

        {groups.length === 0 ? (
          <EmptyState title="No PCR fields imported yet" detail="Import a PCR file for this product to see its fields here." />
        ) : (
          groups.map((group) => (
            <details key={group.category} className="mim-collapse-section" open>
              <summary>{group.category}</summary>
              <FieldList rows={group.rows} />
            </details>
          ))
        )}

        <details className="mim-collapse-section" open>
          <summary>PCR snapshot{latestPcr ? ` (v${latestPcr.version} - ${latestPcr.status})` : ''}</summary>
          {!latestPcr ? (
            <EmptyState title="No PCR record yet" detail="Create a PCR from this product's attributes first." />
          ) : !pcrFields ? (
            <div className="mim-note">Loading PCR fields...</div>
          ) : pcrFields.fieldCount === 0 ? (
            <EmptyState title="This PCR has no fields" detail="Something else may need fixing - see facts breakdown below." />
          ) : (
            <FieldList rows={pcrFields.fields.map((f) => ({ label: f.displayName || f.canonicalFieldKey, value: f.normalizedValue || f.rawValue || '-' }))} />
          )}
        </details>

        <details className="mim-collapse-section">
          <summary>Compare across variants</summary>
          {approvedPcrRecords.length < 2 ? (
            <div className="mim-note">Only one approved PCR exists for this product - nothing to compare yet.</div>
          ) : !showVariantMatrix ? (
            <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowVariantMatrix(true)}>
              Compare all {approvedPcrRecords.length} approved variant PCRs
            </button>
          ) : variantMatrix?.isLoading ? (
            <div className="mim-note">Loading variant fields...</div>
          ) : (
            <div className="mim-table-wrap">
              <table className="mim-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    {variantMatrix?.columns.map((c) => <th key={c.pcrId}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {variantMatrix?.rows.map((row) => (
                    <tr key={row.key}>
                      <td><strong>{row.displayName}</strong></td>
                      {row.values.map((value, i) => <td key={i}>{value}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>

        <details className="mim-collapse-section">
          <summary>Kingspan facts by variant</summary>
          {!factsSummary || factsSummary.variants.length === 0 ? (
            <EmptyState title="No canonicalized Kingspan facts yet" detail="Facts sync after import; check back if you just imported." />
          ) : (
            <div className="mim-stack">
              {factsSummary.variants.map((v) => (
                <div key={v.productVariantId ?? 'none'} className="rules-selected-pack">
                  <div>
                    <strong>{v.variantLabel}</strong>
                    <span>{v.fieldCount} field{v.fieldCount === 1 ? '' : 's'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </details>
      </MimCard>
    </aside>
  )
}

function valueOf(item: ProductDataItemDto) {
  return [item.value, item.unit].filter(Boolean).join(' ') || '-'
}

function collapseByValue(items: ProductDataItemDto[]): Array<{ label: string; value: string }> {
  const byLabel = new Map<string, ProductDataItemDto[]>()
  for (const item of items) {
    const label = item.displayLabel || item.attributeName
    const list = byLabel.get(label) ?? []
    list.push(item)
    byLabel.set(label, list)
  }

  const rows: Array<{ label: string; value: string }> = []
  for (const [label, group] of byLabel.entries()) {
    const byValue = new Map<string, string[]>()
    for (const item of group) {
      const value = valueOf(item)
      const variant = item.variantLabel || (item.productVariantId ? item.productVariantId.slice(0, 8) : null)
      const variants = byValue.get(value) ?? []
      if (variant) variants.push(variant)
      byValue.set(value, variants)
    }

    if (byValue.size <= 1) {
      rows.push({ label, value: valueOf(group[0]) })
      continue
    }

    for (const [value, variants] of byValue.entries()) {
      const distinctVariants = Array.from(new Set(variants))
      rows.push({
        label: distinctVariants.length > 0 ? `${label} (${distinctVariants.join(', ')})` : label,
        value,
      })
    }
  }
  return rows.sort((a, b) => a.label.localeCompare(b.label))
}
